import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';

/**
 * A cached data-fetching hook built on React Query.
 *
 * @param {string|null} url - The API URL to fetch. Pass null to disable the query.
 * @param {object} options - Extra React Query options (staleTime, gcTime, etc.)
 *
 * Caching behaviour:
 *  - staleTime  : data is considered fresh for this duration → NO refetch on remount / navigation
 *  - gcTime     : unused cache entries are garbage-collected after this duration
 *
 * Default staleTime (5 min) is suitable for slow-changing reference data such as
 * regions, departments, arrondissements, enterprise labels and the current user profile.
 * For frequently-changing paginated lists, pass a shorter staleTime (e.g. 30_000).
 */
const useAppQuery = (url, options = {}) => {
  const authHeader = useAuthHeader();

  const { staleTime = 5 * 60 * 1000, ...restOptions } = options;

  const query = useQuery({
    queryKey: [url],
    queryFn: async () => {
      const response = await axios.get(url, {
        headers: { Authorization: authHeader() },
      });
      return response.data;
    },
    enabled: !!url,
    staleTime,
    ...restOptions,
  });

  // Return a named object for flexibility and readability.
  // Callers can destructure only what they need: const { data, isLoading } = useAppQuery(url)
  return {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    data: query.data,
    refetch: query.refetch,
  };
};

export default useAppQuery;
